
import React from 'react';
import { Play, SkipBack, ChevronRight, Settings, AlertTriangle, Zap, Clock, Mail, ShieldAlert } from 'lucide-react';
import * as AllIcons from 'lucide-react';
import { AppNode, SimulationState, Edge, NodeCategory } from '../../types';
import { useEditorConfig } from '../../contexts/EditorContext';
import { UserTaskSimulationForm } from '../forms/simulation/UserTaskSimulationForm';
import { ServiceTaskSimulationForm } from '../forms/simulation/ServiceTaskSimulationForm';
import { GatewaySimulationForm } from '../forms/simulation/GatewaySimulationForm';
import { GenericSimulationForm } from '../forms/simulation/GenericSimulationForm';
import { ScriptTaskSimulationForm } from '../forms/simulation/ScriptTaskSimulationForm';


interface SimulationOverlayProps {
  simulationState: SimulationState;
  nodes: AppNode[];
  edges: Edge[];
  onAction: (action: 'NEXT' | 'BACK' | 'STOP' | 'SELECT_START' | 'PROCESS_QUEUE', data?: any) => void;
  suggestedPath?: string | null; // For gateways, which path evaluated to true
}

const SimulationOverlay: React.FC<SimulationOverlayProps> = ({ 
  simulationState, 
  nodes, 
  edges,
  onAction,
  suggestedPath
}) => {
  const { nodeTypes, componentRegistry } = useEditorConfig();

  const currentNode = nodes.find(n => n.id === simulationState.currentNodeId);
  const config = currentNode ? nodeTypes[currentNode.type || ''] : null;

  // --- Render Functions ---

  const renderStartSelection = () => {
      const startNodes = nodes.filter(n => n.type?.endsWith('StartEvent'));
      return (
          <div className="flex flex-col h-full w-full bg-slate-50 dark:bg-slate-950">
              {/* Header */}
              <div className="p-6 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
                  <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-teal-50 dark:bg-teal-900/20 text-teal-600 rounded-lg flex items-center justify-center border border-teal-100 dark:border-teal-900">
                          <Play size={20} fill="currentColor"/>
                      </div>
                      <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                          Start Simulation
                      </h2>
                  </div>
                  <p className="text-xs text-slate-500 leading-relaxed">
                      Select an entry point to begin simulating this workflow.
                  </p>
              </div>

              {/* Start Nodes List */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {startNodes.map(node => (
                      <button
                        key={node.id}
                        onClick={() => onAction('SELECT_START', { nodeId: node.id })}
                        className="w-full flex items-center gap-3 p-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:border-teal-500 hover:ring-1 hover:ring-teal-500/20 transition-all group text-left shadow-sm"
                      >
                          <div className="w-10 h-10 rounded-lg bg-slate-50 dark:bg-slate-800 text-teal-600 flex items-center justify-center shrink-0 border border-slate-100 dark:border-slate-700">
                              <Play size={16} fill="currentColor" />
                          </div>
                          <div className="flex-1 min-w-0">
                                <div className="font-bold text-slate-800 dark:text-slate-200 text-sm truncate">{node.data.label}</div>
                                <div className="text-[10px] text-slate-400 font-mono truncate">{node.type}</div>
                          </div>
                          <ChevronRight className="text-slate-300 group-hover:text-teal-500" size={16} />
                      </button>
                  ))}
                  
                  {startNodes.length === 0 && (
                      <div className="text-center py-8 text-slate-400 text-xs">
                          No start events found in this process.
                      </div>
                  )}
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-center">
                   <button 
                      onClick={() => onAction('STOP')} 
                      className="text-xs font-medium text-red-500 hover:text-red-700 hover:underline"
                   >
                      Cancel & Exit
                   </button>
              </div>
          </div>
      );
  };

  const renderActiveNodeContent = () => {
      if (!currentNode || !config) return null;

      const category = config.category;
      const type = currentNode.type;
      
      const isUserTask = type === 'UserTask';
      const isFormStart = type === 'FormStartEvent';
      const isScriptTask = type === 'ScriptTask';
      
      const isServiceTask = category === 'Tasks' && !isUserTask && !isScriptTask;
      const isMessageStart = type === 'MessageStartEvent' || type === 'SignalStartEvent';

      const isGateway = category === 'Gateways';
      const isEnd = category === 'End Events';

      // Find boundary events attached to this node
      const attachedBoundaries = nodes.filter(n => 
          n.parentNode === currentNode.id && 
          n.type?.endsWith('BoundaryEvent')
      );

      // Dynamic Icon
      // @ts-ignore
      const IconComponent = AllIcons[config.icon] || Settings;

      const handleNext = (data?: any) => {
          onAction('NEXT', data);
      };

      const handleTriggerBoundary = (boundary: AppNode) => {
          onAction('NEXT', { 
              triggerBoundaryId: boundary.id, 
              boundaryLabel: boundary.data.label || boundary.type 
          });
      };

      return (
          <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-950">
              {/* Header */}
              <div className="flex items-center gap-4 px-6 py-4 border-b border-slate-200 dark:border-slate-800 shrink-0 bg-white dark:bg-slate-900">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-sm" style={{ color: config.color }}>
                      <IconComponent size={20} />
                  </div>
                  <div className="flex-1 min-w-0">
                      <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5 flex items-center gap-2">
                         Step <span className="bg-slate-100 dark:bg-slate-800 text-slate-500 px-1.5 py-0.5 rounded border border-slate-200 dark:border-slate-700 font-mono">{currentNode.type}</span>
                      </div>
                      <h3 className="text-sm font-bold text-slate-900 dark:text-white truncate" title={currentNode.data.label}>{currentNode.data.label}</h3>
                  </div>
                  
                  <button 
                      onClick={() => onAction('BACK')}
                      disabled={simulationState.history.length === 0}
                      className="p-2 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                      title="Back one step"
                  >
                      <SkipBack size={16} />
                  </button>
              </div>

              {/* Boundary Events Section */}
              {attachedBoundaries.length > 0 && (
                  <div className="px-6 py-3 bg-amber-50 dark:bg-amber-900/10 border-b border-amber-100 dark:border-amber-900/30">
                      <div className="flex items-center gap-2 text-[10px] font-bold text-amber-600 dark:text-amber-400 uppercase tracking-widest mb-2">
                          <ShieldAlert size={12} /> Interruption Triggers
                      </div>
                      <div className="grid grid-cols-1 gap-2">
                          {attachedBoundaries.map(boundary => {
                              const bConfig = nodeTypes[boundary.type || ''];
                              // @ts-ignore
                              const BIcon = AllIcons[bConfig?.icon || 'Zap'] || Zap;
                              return (
                                  <button
                                      key={boundary.id}
                                      onClick={() => handleTriggerBoundary(boundary)}
                                      className="flex items-center gap-3 p-2 bg-white dark:bg-slate-900 border border-amber-200 dark:border-amber-800 rounded-lg hover:border-amber-500 hover:bg-amber-50 transition-all group text-left shadow-sm"
                                  >
                                      <div className="p-1.5 bg-amber-50 dark:bg-amber-900/30 text-amber-600 rounded" style={{ color: bConfig?.color }}>
                                          <BIcon size={14} />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                          <div className="text-xs font-bold text-slate-700 dark:text-slate-200 truncate">
                                              {boundary.data.label || bConfig?.label}
                                          </div>
                                          <div className="text-[9px] text-amber-600 font-medium">Trigger Boundary Event</div>
                                      </div>
                                      <ChevronRight className="text-slate-300 group-hover:text-amber-500" size={14} />
                                  </button>
                              );
                          })}
                      </div>
                  </div>
              )}

              {/* Node Execution Form */}
              <div className="flex-1 overflow-hidden relative">
                    {/* Check for custom simulation form first */}
                    {componentRegistry['SimulationForm:' + type] ? (
                        React.createElement(componentRegistry['SimulationForm:' + type], {
                            node: currentNode,
                            onNext: handleNext,
                            variables: simulationState.variables
                        })
                    ) : (isUserTask || isFormStart) ? (
                        <UserTaskSimulationForm 
                            node={currentNode} 
                            onNext={handleNext} 
                        />
                    ) : isScriptTask ? (
                        <ScriptTaskSimulationForm
                            node={currentNode}
                            onNext={handleNext}
                            variables={simulationState.variables}
                        />
                    ) : (isServiceTask || isMessageStart) ? (
                        <ServiceTaskSimulationForm 
                            node={currentNode} 
                            onNext={handleNext} 
                            variables={simulationState.variables}
                        />
                    ) : isGateway ? (
                        <GatewaySimulationForm 
                            node={currentNode} 
                            edges={edges}
                            onNext={handleNext} 
                            suggestedPath={suggestedPath || null}
                            visitedEdgeIds={simulationState.visitedEdgeIds}
                            executionQueueCount={simulationState.executionQueue.length}
                            executionQueue={simulationState.executionQueue}
                            onProcessQueue={() => onAction('PROCESS_QUEUE')}
                        />
                    ) : (
                        <GenericSimulationForm 
                            node={currentNode} 
                            onNext={handleNext} 
                            isEnd={isEnd}
                            onExit={() => onAction('STOP')}
                        />
                    )}
              </div>
          </div>
      );
  };

  return (
    <div className="h-full w-full text-slate-900 font-sans">
        {simulationState.currentNodeId === null ? renderStartSelection() : renderActiveNodeContent()}
    </div>
  );
};

export default SimulationOverlay;
